import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NoSymbolIcon from "@heroicons/react/24/outline/NoSymbolIcon";
import LoadFailure from "../../components/Load/LoadFailure";
import { PageLoader } from "../../components/Loading";
import { FeaturesProvider, useFeatures } from "./FeaturesContext";
import { startPage } from "../../utils/authUtils";
import RegistrationClosed from "./RegistrationClosed";
import { CLOSED_BAND, CLOSED_MESSAGE } from "./closedScreen";

/**
 * A screen of a flagged module: shown only while its module is switched on. Off, the screen says so - a legitimate
 * answer, unlike an empty state (owner, 15 Oct 2026) - and nothing of the module is asked for. If whether it is on
 * couldn't be checked, that is said instead: the module may well be on. Held back by this build (FeaturesContext), it
 * says that.
 */
export function ModuleGate({ module, publicPage = false, children }) {
  const { t } = useTranslation();
  const features = useFeatures();
  if (!module) return children;
  if (features.status === "failed") {
    return <LoadFailure load={features.load} title={t("featuresLoadFailedTitle")}
                        bodies={{ REFUSED: t("featuresLoadFailedBody"), NOT_FOUND: t("featuresLoadFailedBody"),
                          FAILED: t("featuresLoadFailedBody") }} />;
  }
  if (features.isHeldBack(module)) return <ModuleHeldBack module={module} publicPage={publicPage} />;
  if (!features.isOn(module)) return <ModuleOff module={module} publicPage={publicPage} />;
  return children;
}

/** A public page of a flagged module (the application form, certificate verification): asks for itself. */
export function PublicModulePage({ module, children }) {
  const { t } = useTranslation();
  return (
    <FeaturesProvider fallback={<PageLoader message={t("loadingApplication")} />}>
      <ModuleGate module={module} publicPage>{children}</ModuleGate>
    </FeaturesProvider>
  );
}

/** Tagged data-module-off: the refusing-server guard's second pass checks a switched-off screen says this. */
export function ModuleOff({ module, publicPage = false }) {
  const { t } = useTranslation();
  // /register: no sign-in link, and the church's contact (the owner's answer 3 of 3 Nov 2026).
  if (module === "selfRegistration") return <RegistrationClosed />;
  return (
    <NotOnTheseScreens tag={{ "data-module-off": module }} title={t("moduleOffTitle")} publicPage={publicPage}>
      <p className="mt-4">{moduleOffSentence(t, module)}</p>
      <p className="mt-2 opacity-70">{t("moduleOffBody")}</p>
    </NotOnTheseScreens>
  );
}

/**
 * A module the server has on that this build holds back (FeaturesContext): some of its screens still don't say a
 * failed load. Not "switched off" - it isn't - and said as what it is. Tagged data-module-held-back.
 */
export function ModuleHeldBack({ module, publicPage = false }) {
  const { t } = useTranslation();
  return (
    <NotOnTheseScreens tag={{ "data-module-held-back": module }} title={t("moduleHeldBackTitle")} publicPage={publicPage}>
      <p className="mt-4">{t("moduleHeldBackBody")}</p>
    </NotOnTheseScreens>
  );
}

function NotOnTheseScreens({ tag, title, publicPage, children }) {
  const { t } = useTranslation();
  return (
    <div className={CLOSED_BAND} {...tag}>
      <div className={CLOSED_MESSAGE} data-closed-message>
        <NoSymbolIcon className="h-20 w-20 inline-block opacity-60" />
        <h1 className="text-2xl font-bold mt-4">{title}</h1>
        {children}
        {/* A public page has no sign-in link (the public pages' session 7; the owner's order of 3 Nov 2026). */}
        {!publicPage && <Link to={startPage()} className="btn btn-primary mt-6">
          {startPage() === "/app/dashboard" ? t("backToDashboard") : t("backToStart")}</Link>}
      </div>
    </div>
  );
}

/** One whole sentence per module - never a module's name fitted into a sentence (each language bends it its own way). */
function moduleOffSentence(t, module) {
  switch (module) {
    case "applications": return t("moduleOffApplications");
    case "bookings": return t("moduleOffBookings");
    case "activities": return t("moduleOffActivities");
    case "registers": return t("moduleOffRegisters");
    case "meetings": return t("moduleOffMeetings");
    case "publicPages": return t("moduleOffPublicPages");
    default: return null;
  }
}

/**
 * Above every signed-in page while it couldn't be checked which modules are switched on: the menu is missing every
 * flagged module's entries then, and the person is told why rather than left to wonder.
 */
export function FeaturesLoadBanner() {
  const { t } = useTranslation();
  const features = useFeatures();
  if (features.status !== "failed") return null;
  return (
    <div className="bg-base-100 px-4 py-1">
      <LoadFailure load={features.load} compact title={t("featuresLoadFailedTitle")}
                   bodies={{ REFUSED: t("featuresLoadFailedMenu"), NOT_FOUND: t("featuresLoadFailedMenu"),
                     FAILED: t("featuresLoadFailedMenu") }} />
    </div>
  );
}

/**
 * Above every signed-in page while this build holds back a module the server has on (FeaturesContext): its menu
 * entries are missing, and whoever switched it on is told why - with the module's name as FEATURES_ON writes it.
 */
export function FeaturesHeldBackBanner() {
  const { t } = useTranslation();
  const { heldBack } = useFeatures();
  if (!heldBack.length) return null;
  return (
    <div role="status" className="bg-warning text-warning-content text-sm text-center px-4 py-1">
      {t("modulesHeldBack")} <code>{heldBack.join(", ")}</code>
    </div>
  );
}
